import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  NOTARY_DOC_LANDINGS,
  getNotaryDocLanding,
  type NotaryDocLanding,
} from "@/content/notary-corpus/documents";

function docFaqsTh(d: NotaryDocLanding) {
  return [
    { q: `${d.titleTh} ใช้ทำอะไรได้บ้าง?`, a: d.useCaseTh },
    { q: `ต้องเตรียมอะไรมาบ้างสำหรับ${d.officialTh}?`, a: d.prepareTh.join(" · ") },
    { q: `ทนายรับรองอะไรให้บ้างในเอกสารนี้?`, a: d.certTypeTh },
    { q: `ข้อผิดพลาดที่ทำให้เอกสารถูกตีกลับคืออะไร?`, a: d.pitfallsTh.join(" · ") },
    { q: `หลังทนายรับรองแล้วต้องทำอะไรต่อ?`, a: d.nextStepTh },
    { q: `ใช้เวลาเท่าไหร่?`, a: `${d.turnaroundTh} · ค่าบริการคิดตามจำนวนฉบับและตราประทับ ขอใบเสนอราคาฟรีก่อนได้ทุกครั้ง` },
    {
      q: "ประเทศไทยออก Apostille ได้หรือยัง?",
      a: "ยังไม่มีผลบังคับใช้ในทางปฏิบัติครับ ประเทศไทยยื่นภาคยานุวัติสารเข้าเป็นภาคีอนุสัญญากรุงเฮกว่าด้วยการยกเลิกการรับรองเอกสาร (Apostille Convention) เมื่อ 30 มิถุนายน 2569 (2026) และอนุสัญญาจะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2570 (2027) ระหว่างนี้เอกสารไทยที่จะใช้ต่างประเทศยังต้องผ่านนิติกรณ์กรมการกงสุล กระทรวงการต่างประเทศ แล้วรับรองต่อที่สถานทูต/สถานกงสุลของประเทศปลายทาง — โปรดตรวจสถานะล่าสุดที่ hcch.net และ consular.mfa.go.th ก่อนดำเนินการ",
    },
    {
      q: "ทนายรับรองเอกสารในไทยเหมือน Notary Public ต่างประเทศไหม?",
      a: "ไทยไม่มีตำแหน่ง Notary Public ตามระบบต่างประเทศ แต่มี 'ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร' (Notarial Services Attorney) ซึ่งขึ้นทะเบียนกับสภาทนายความในพระบรมราชูปถัมภ์ตามข้อบังคับ พ.ศ. 2551 และเป็นผู้รับรองที่หน่วยงานต่างประเทศยอมรับ",
    },
    {
      q: "ต้องมาเซ็นเองหรือมอบให้คนอื่นมาแทนได้?",
      a: "การรับรองลายมือชื่อต้องลงนามต่อหน้าทนายด้วยตนเองพร้อมหนังสือเดินทางหรือบัตรประชาชนตัวจริง ส่วนการรับรองสำเนาถูกต้องต้องนำต้นฉบับมาแสดง",
    },
  ];
}

export const Route = createFileRoute("/notary/document/$doc")({
  loader: ({ params }) => {
    const d = getNotaryDocLanding(params.doc);
    if (!d) throw notFound();
    return { d };
  },
  component: NotaryDocPageTH,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบประเภทเอกสาร" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { d } = loaderData;
    const title = `${d.titleTh} 2026 — ทนาย Notarial Services Attorney`;
    const description = `${d.titleTh} โดยทนายผู้ทำคำรับรองลายมือชื่อและเอกสาร — เอกสารที่ต้องเตรียม ขั้นตอนต่อจากนี้ และ ${d.turnaroundTh}`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/notary/document/${params.doc}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/notary/document/${params.doc}` }],
        keywords: [
          d.titleTh,
          `${d.titleTh} ราคา`,
          `notary public ${d.titleTh}`,
          "ทนายรับรองเอกสาร",
          "ทนายรับรองลายมือชื่อ",
          "notarial services attorney",
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "ทนาย Notary Public", url: `${site}/notary` },
              { name: d.titleTh, url: `${site}/notary/document/${params.doc}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(docFaqsTh(d))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/notary/document/${params.doc}`)) },
      ],
    };
  },
});

function NotaryDocPageTH() {
  const { d } = Route.useLoaderData();
  const faqs = docFaqsTh(d);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/notary" className="hover:underline">ทนาย Notary Public</a> <span>/</span>{" "}
          <span>{d.titleTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{d.titleTh} (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          เอกสารอ้างอิง: <strong>{d.officialTh}</strong> — ประเภทคำรับรอง: {d.certTypeTh}
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/notary/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">คำถามที่พบบ่อย Notary</a>
          <a href={`/en/notary/document/${d.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ใช้ทำอะไร / ยื่นกับใคร</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.useCaseTh}</p>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">เตรียมเอกสารก่อนเข้าพบทนาย</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.prepareTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
          <p className="mt-4 text-sm text-muted-foreground"><strong>ระยะเวลา:</strong> {d.turnaroundTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ข้อผิดพลาดที่ทำให้เอกสารถูกตีกลับ</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.pitfallsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">ขั้นตอนถัดไปหลังทนายรับรอง</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.nextStepTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — {d.titleTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">รับรองเอกสารประเภทอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_DOC_LANDINGS.filter((x) => x.slug !== d.slug).map((x) => (
              <a key={x.slug} href={`/notary/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          กรอบวิชาชีพอ้างอิง: ข้อบังคับสภาทนายความว่าด้วยการขึ้นทะเบียนทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร พ.ศ. 2551 ·
          ขั้นตอนนิติกรณ์อ้างอิงกองสัญชาติและนิติกรณ์ กรมการกงสุล — ข้อกำหนดและระยะเวลาอาจเปลี่ยนแปลง
          โปรดตรวจสอบกับหน่วยงานเจ้าของเรื่องก่อนดำเนินการทุกครั้ง
        </p>

        <PageGallery category="notary" seed="notary-document" locale="th" />
        <FaqSourceNote topic="notary" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}